import { createContext, useContext, type ReactNode, useEffect } from "react";
import type {
  CopyFeedbackHandler,
  CopyFeedbackState,
} from "../hooks/useCopyFeedback";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import { CopyFeedbackIndicator } from "./CopyFeedbackIndicator";
import { subscribeToCopyFeedback } from "../utils/copyFeedbackBus";

interface CopyFeedbackContextValue {
  feedback: CopyFeedbackState | null;
  triggerFeedback: CopyFeedbackHandler;
  clearFeedback: () => void;
}

const CopyFeedbackContext = createContext<CopyFeedbackContextValue | null>(
  null,
);

export function CopyFeedbackProvider({
  children,
}: {
  children: ReactNode;
}) {
  const copyFeedback = useCopyFeedback();

  useEffect(() => {
    return subscribeToCopyFeedback(copyFeedback.triggerFeedback);
  }, [copyFeedback.triggerFeedback]);

  return (
    <CopyFeedbackContext.Provider value={copyFeedback}>
      {children}
      {copyFeedback.feedback && (
        <CopyFeedbackIndicator
          feedback={copyFeedback.feedback}
          className="pointer-events-none fixed top-5 left-0 right-0 flex justify-center z-50"
        />
      )}
    </CopyFeedbackContext.Provider>
  );
}

export function useCopyFeedbackContext() {
  const context = useContext(CopyFeedbackContext);
  if (!context) {
    throw new Error(
      "useCopyFeedbackContext must be used within a CopyFeedbackProvider",
    );
  }
  return context;
}
